<template>
  <v-app id="inspire" class="pw-app">
    <router-view />
  </v-app>
</template>

<script>
const CryptoJS = require("crypto-js");

export default {
  data() {
    return {
      isTabClosed: false,

      logoutTimer: null, // Timer for automatic logout
      inactivityDuration: 10 * 60 * 1000, // 10 minutes in milliseconds
    };
  },
  methods: {
    logout() {
      this.logOut();
      location.replace("/");
    },
    encryptIt(string) {
      let encrypted = CryptoJS.AES.encrypt(
        `${string}`,
        "secret key"
      ).toString();
      return encrypted;
    },
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");

/* ==========================================================================
   Design tokens
   ========================================================================== */
:root {
  --pw-canvas: #f5f7fa;
  --pw-surface: #ffffff;
  --pw-border: #e4e8ee;
  --pw-border-strong: #d3dae3;
  --pw-text: #1a2436;
  --pw-text-muted: #66738a;
  --pw-text-faint: #8d99ad;
  --pw-brand: #1f4fa3;
  --pw-brand-soft: #eef3fb;

  --pw-radius-sm: 8px;
  --pw-radius: 12px;
  --pw-radius-lg: 16px;

  --pw-shadow-sm: 0 1px 2px rgba(16, 24, 40, 0.05);
  --pw-shadow: 0 1px 3px rgba(16, 24, 40, 0.07), 0 1px 2px rgba(16, 24, 40, 0.04);
  --pw-shadow-lg: 0 8px 24px rgba(16, 24, 40, 0.08);
}

/* ==========================================================================
   Base
   ========================================================================== */
.pw-app,
.pw-app .v-application--wrap {
  font-family: "Inter", -apple-system, "Segoe UI", Roboto, sans-serif !important;
  background: var(--pw-canvas) !important;
  color: var(--pw-text);
}

.pw-app .v-main {
  background: var(--pw-canvas);
}

.pw-app h1,
.pw-app h2,
.pw-app h3,
.pw-app h4 {
  letter-spacing: -0.015em;
  color: var(--pw-text);
}

/* ==========================================================================
   Cards - flatten the old rounded xxl + elevation 6 look
   ========================================================================== */
.pw-app .v-card,
.pw-app .v-card.rounded-xxl,
.pw-app .v-sheet.v-card {
  border-radius: var(--pw-radius-lg) !important;
  box-shadow: var(--pw-shadow) !important;
  border: 1px solid var(--pw-border);
  background: var(--pw-surface);
}

.pw-app .v-dialog > .v-card {
  border-radius: var(--pw-radius-lg) !important;
  box-shadow: var(--pw-shadow-lg) !important;
}

/* Page header inside every list/detail card */
.pw-app .v-card > .v-card__title {
  font-size: 1.0625rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  padding: 18px 22px 12px;
  gap: 8px;
  align-items: center;
}

.pw-app .v-card > .v-card__title h1,
.pw-app .v-card > .v-card__title h2,
.pw-app .v-card > .v-card__title h3 {
  font-size: 1.0625rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.4;
}

.pw-app .v-card > .v-card__subtitle {
  color: var(--pw-text-muted);
  font-size: 0.8125rem;
  padding-top: 0;
}

.pw-app .v-card > .v-card__text {
  padding: 8px 22px 20px;
  color: var(--pw-text);
  font-size: 0.875rem;
}

/* ==========================================================================
   Buttons
   ========================================================================== */
.pw-app .v-btn {
  border-radius: var(--pw-radius) !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600;
  box-shadow: none !important;
}

.pw-app .v-btn--is-elevated {
  box-shadow: var(--pw-shadow-sm) !important;
}

.pw-app .v-btn--fab,
.pw-app .v-btn--round {
  border-radius: 50% !important;
}

/* The many small action fabs in tables read better as soft squares */
.pw-app .v-card .v-btn--fab.v-size--x-small {
  border-radius: 10px !important;
  height: 32px;
  width: 32px;
}

.pw-app .v-btn--outlined {
  border-color: var(--pw-border-strong) !important;
}

/* ==========================================================================
   Inputs - replace heavy solo rounded pills with clean bordered fields
   ========================================================================== */
.pw-app .v-input .v-input__slot {
  border-radius: var(--pw-radius) !important;
  box-shadow: none !important;
  min-height: 42px;
}

.pw-app .v-text-field--solo > .v-input__control > .v-input__slot,
.pw-app .v-select.v-text-field--solo > .v-input__control > .v-input__slot,
.pw-app .v-autocomplete.v-text-field--solo > .v-input__control > .v-input__slot {
  background: var(--pw-surface) !important;
  border: 1px solid var(--pw-border-strong);
  box-shadow: none !important;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.pw-app .v-text-field--solo.v-input--is-focused > .v-input__control > .v-input__slot,
.pw-app .v-input--is-focused .v-input__slot {
  border-color: var(--pw-brand) !important;
  box-shadow: 0 0 0 3px rgba(31, 79, 163, 0.12) !important;
}

.pw-app .v-text-field--outlined fieldset {
  border-color: var(--pw-border-strong);
  border-radius: var(--pw-radius) !important;
}

.pw-app .v-label {
  font-size: 0.875rem;
  color: var(--pw-text-muted);
}

.pw-app .v-input input,
.pw-app .v-input textarea {
  font-size: 0.875rem;
}

/* ==========================================================================
   Tables
   ========================================================================== */
.pw-app .v-data-table,
.pw-app .v-simple-table {
  border-radius: var(--pw-radius) !important;
  border: 1px solid var(--pw-border);
  overflow: hidden;
  background: var(--pw-surface);
}

.pw-app .v-data-table thead th,
.pw-app .v-simple-table thead th {
  background: #fafbfd !important;
  color: var(--pw-text-faint) !important;
  font-size: 0.6875rem !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
  border-bottom: 1px solid var(--pw-border) !important;
  height: 42px;
}

.pw-app .v-data-table tbody td,
.pw-app .v-simple-table tbody td {
  font-size: 0.8125rem;
  color: var(--pw-text);
  border-bottom: 1px solid #f0f2f6 !important;
  height: 50px;
}

.pw-app .v-data-table tbody tr:hover,
.pw-app .v-simple-table tbody tr:hover {
  background: var(--pw-brand-soft) !important;
}

/* Neutralise the old zebra striping - hover is enough */
.pw-app .striped-table .table-row-even,
.pw-app .striped-table .table-row-odd {
  background-color: transparent;
}

.pw-app .striped-table tbody tr:last-child td {
  border-bottom: none !important;
}

/* ==========================================================================
   Chips, badges, pagination
   ========================================================================== */
.pw-app .v-chip {
  border-radius: 999px !important;
  font-weight: 600;
  font-size: 0.75rem;
  letter-spacing: 0;
}

.pw-app .v-pagination__item,
.pw-app .v-pagination__navigation {
  border-radius: var(--pw-radius-sm) !important;
  box-shadow: none !important;
  border: 1px solid var(--pw-border);
  font-weight: 600;
}

.pw-app .v-pagination__item--active {
  border-color: var(--pw-brand);
}

/* ==========================================================================
   Utilities kept from the previous stylesheet
   ========================================================================== */
.sr_no {
  border-top-left-radius: 4px !important;
}

.hover-primary:hover {
  background-color: var(--v-primary-base) !important;
  color: white !important;
}

.action_th {
  min-width: 115px;
}

.striped-table .table-row-last {
  border-end-end-radius: 4px !important;
}

.wrap-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Scrollbars */
.pw-app ::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

.pw-app ::-webkit-scrollbar-thumb {
  background: #ccd4df;
  border-radius: 999px;
  border: 3px solid transparent;
  background-clip: content-box;
}

.pw-app ::-webkit-scrollbar-thumb:hover {
  background: #aab6c6;
  background-clip: content-box;
}

@media print {
  .dont-print-header {
    display: none !important;
  }
  /* Vuetify pads v-main inline for the drawer and app bar; with those
     hidden the padding would push printed pages into a narrow column */
  .v-main {
    padding: 0 !important;
  }
  .v-application,
  .v-application--wrap {
    min-height: 0 !important;
    background: #fff !important;
  }
}
</style>
